'use client'

import { GuestRoomSelector } from '@/src/components/common/home/hotelSearch/guestRoomSelector'
import { HotelGuestsSelector } from '@/src/components/common/home/hotelSearch/hotelGuestsSelector'
import type { PropertyOccupancy } from '@/src/hooks/hotels/usePropertyOccupancy'
import { Box, Button, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

interface PropertyGuestsFieldProps {
    variant: 'desktop' | 'mobile'
    occupancy: PropertyOccupancy
}

/**
 * Rooms and guests on the property (SEO) page, reusing the selectors the home search form
 * already has — the popover on desktop, the drawer on mobile.
 *
 * Renders the value only. Every caller already draws its own labels, so labelling here too
 * printed "Rooms & Guests" twice inside the date popup.
 *
 * Closing the selector commits the draft, which is what makes a search run once prices are
 * on screen. Nothing runs before the first search; the Search rooms button does that.
 */
const PropertyGuestsField: React.FC<PropertyGuestsFieldProps> = ({ variant, occupancy }) => {
    const t = useTranslations('common')
    const [open, setOpen] = useState(false)
    const { rooms, limits, setRoomCount, setRoomAdults, setRoomChildren, applyRooms } = occupancy

    const close = () => {
        setOpen(false)
        applyRooms()
    }

    const controls = {
        roomConfigs: rooms,
        ...limits,
        onRoomCountChange: setRoomCount,
        onRoomAdultsChange: setRoomAdults,
        onRoomChildrenChange: setRoomChildren
    }

    if (variant === 'desktop') {
        return <GuestRoomSelector {...controls} active={open} onActivate={() => setOpen(true)} onClose={close} />
    }

    const guestCount = rooms.reduce((total, room) => total + room.adults + room.children, 0)
    const summary = `${rooms.length} ${t('room', { s: rooms.length > 1 ? 's' : '' })}, ${guestCount} ${t('guest', { s: guestCount !== 1 ? 's' : '' })}`

    return (
        <>
            <Button
                variant="text"
                onClick={() => setOpen(true)}
                sx={{
                    textTransform: 'none',
                    width: '100%',
                    justifyContent: 'flex-start',
                    p: 0,
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'textColors.primary'
                }}
            >
                <Box
                    component="span"
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {summary}
                </Box>
            </Button>
            <HotelGuestsSelector {...controls} open={open} onOpen={() => setOpen(true)} onClose={close} />
        </>
    )
}

export { PropertyGuestsField }
export default PropertyGuestsField
